import type { AccessControlProvider } from '@refinedev/core'
import type { SessionInfo } from '@sirius/contracts/admin'
import {
  ACTIVITY_VIEWER_ROLES,
  CABINET_CREDIT_ROLES,
  DASHBOARD_VIEWER_ROLES,
  GAME_CONFIGURATION_ROLES,
  MEMBER_MANAGER_ROLES,
  ROUND_VIEWER_ROLES,
  TENANT_DEFAULT_CONFIGURATION_ROLES,
  TENANT_SETTINGS_VIEWER_ROLES,
  TENANT_UPDATE_ROLES,
} from '@sirius/contracts/admin'
import {
  ACTIVITY_RESOURCE,
  PLATFORM_ACTIVITY_RESOURCE,
  PLATFORM_SETTINGS_RESOURCE,
  ROUNDS_RESOURCE,
} from './dataProvider'

/**
 * Platform pages only a platform admin opens. Everything else is tenant data
 * the backend scopes. `games` and `gameConfigurations` are the games and
 * configuration pages, `platformSettings` the platform's defaults
 * (Platform › Settings). A technician reaches the platform's tenants (list
 * and show) and nothing else here (`TECHNICIAN_TENANT_ACTIONS`).
 */
export const PLATFORM_ADMIN_ONLY_RESOURCES = [
  'users',
  'games',
  PLATFORM_ACTIVITY_RESOURCE,
  'gameConfigurations',
  PLATFORM_SETTINGS_RESOURCE,
] as const

/** What a technician may do with the platform's tenants: read them. */
export const TECHNICIAN_TENANT_ACTIONS: readonly string[] = ['list', 'show']

/**
 * The cabinet history: the events of one cabinet (`audit.list` through
 * `recordActivity`). Platform admins, the host tenant's owners/admins and a
 * technician read it (the API gives a technician cabinet events only).
 */
export const CABINET_ACTIVITY_RESOURCE = 'cabinetActivity'

/**
 * The history of a tenant's, location's or user's record: no technician
 * (the API gives it cabinet events only).
 */
export const RECORD_HISTORY_RESOURCE = 'recordHistory'

type AccessInfo = Pick<SessionInfo, 'platformRole' | 'memberships'>

/**
 * Whether the user may read a tenant's data that only some roles see, on the
 * host of `tenantId`: a platform admin always, a tenant user when their role
 * in that tenant is one of `roles` (an owner elsewhere does not count here).
 * A technician never does: the API refuses it everything but cabinets.
 */
export function hasHostRole(
  { platformRole, memberships }: AccessInfo,
  tenantId: string | undefined,
  roles: readonly string[],
) {
  if (platformRole === 'admin') {
    return true
  }
  if (platformRole === 'technician') {
    return false
  }
  const role = memberships.find(
    (membership) => membership.tenantId === tenantId,
  )?.role
  return role !== undefined && roles.includes(role)
}

/**
 * Whether the user may credit in and cash out the cabinets on the host of
 * `tenantId` (`cabinets.creditIn` / `cashOut`): a platform admin or a
 * technician always, a tenant user when their role in that tenant is one of
 * `CABINET_CREDIT_ROLES`.
 */
export function canCreditCabinets(
  info: AccessInfo,
  tenantId: string | undefined,
) {
  return (
    info.platformRole !== null ||
    hasHostRole(info, tenantId, CABINET_CREDIT_ROLES)
  )
}

/**
 * The dashboard (`/`, `dashboard.*` through the client, so no data provider
 * resource): staff and the host tenant's owners/admins.
 */
export const DASHBOARD_RESOURCE = 'dashboard'

/**
 * The host tenant's Settings (`/settings`, `tenants.get` / `update` on the
 * host's tenant): staff and the tenant's owners/admins open them (`list`),
 * staff and its owners change them (`edit`).
 */
export const SETTINGS_RESOURCE = 'settings'

/**
 * The host tenant's members and open invitations and Invite (Settings ›
 * Members, `members.list` / `invitations.*` through the client): staff and
 * the tenant's owners/admins (`MEMBER_MANAGER_ROLES`).
 */
export const MEMBERS_RESOURCE = 'members'

/**
 * The host tenant's default configuration of each game (Settings ›
 * Configurations, `tenants.defaultConfigurations.*` through the client):
 * staff and the tenant's owners/admins (`TENANT_DEFAULT_CONFIGURATION_ROLES`)
 * read and set them.
 */
export const DEFAULT_CONFIGURATIONS_RESOURCE = 'defaultConfigurations'

/**
 * The action on `cabinets` that picks the configuration a cabinet's games
 * run (`gameConfigs` on `cabinets.create` / `update`): staff and the host
 * tenant's owners/admins (`GAME_CONFIGURATION_ROLES`).
 */
export const CONFIGURE_CABINETS_ACTION = 'configure'

/** Resources only staff and some roles of the host's tenant may read. */
const ROLE_SCOPED_RESOURCES = new Map<string, readonly string[]>([
  [DASHBOARD_RESOURCE, DASHBOARD_VIEWER_ROLES],
  [RECORD_HISTORY_RESOURCE, ACTIVITY_VIEWER_ROLES],
  [ROUNDS_RESOURCE, ROUND_VIEWER_ROLES],
  [MEMBERS_RESOURCE, MEMBER_MANAGER_ROLES],
  [DEFAULT_CONFIGURATIONS_RESOURCE, TENANT_DEFAULT_CONFIGURATION_ROLES],
])

export function createAccessControlProvider({
  getMe,
  getTenantId,
}: {
  /** `auth.me` (cached, see sessionQueries.ts) */
  getMe: () => Promise<AccessInfo>
  /** the host's tenant (`tenantRef`) */
  getTenantId: () => string | undefined
}): AccessControlProvider {
  return {
    can: async ({ resource, action }) => {
      if (
        resource !== undefined &&
        (PLATFORM_ADMIN_ONLY_RESOURCES as readonly string[]).includes(resource)
      ) {
        return { can: (await getMe()).platformRole === 'admin' }
      }
      // the Platform section: a technician sees its Tenants, read only
      if (resource === 'platform') {
        return { can: (await getMe()).platformRole !== null }
      }
      if (resource === 'tenants') {
        const { platformRole } = await getMe()
        return {
          can:
            platformRole === 'admin' ||
            (platformRole === 'technician' &&
              TECHNICIAN_TENANT_ACTIONS.includes(action)),
        }
      }
      // a technician reads the locations of every tenant, never changes them
      if (
        resource === 'locations' &&
        (action === 'create' || action === 'edit' || action === 'delete')
      ) {
        return { can: (await getMe()).platformRole !== 'technician' }
      }
      // the activity log and a cabinet's history: a technician reads the
      // cabinet events (the API narrows them)
      if (
        resource === CABINET_ACTIVITY_RESOURCE ||
        resource === ACTIVITY_RESOURCE
      ) {
        const info = await getMe()
        return {
          can:
            info.platformRole === 'technician' ||
            hasHostRole(info, getTenantId(), ACTIVITY_VIEWER_ROLES),
        }
      }
      if (resource === SETTINGS_RESOURCE) {
        const settingsRoles =
          action === 'edit' ? TENANT_UPDATE_ROLES : TENANT_SETTINGS_VIEWER_ROLES
        return { can: hasHostRole(await getMe(), getTenantId(), settingsRoles) }
      }
      // staff, and owners/admins of the host's tenant (dashboard, activity,
      // rounds, members, default configurations)
      const roles =
        resource === undefined ? undefined : ROLE_SCOPED_RESOURCES.get(resource)
      if (roles !== undefined) {
        return { can: hasHostRole(await getMe(), getTenantId(), roles) }
      }
      // the credit buttons on a cabinet's page (no route of its own)
      if (resource === 'cabinets' && action === 'credit') {
        return { can: canCreditCabinets(await getMe(), getTenantId()) }
      }
      // the cabinet form's configuration per game (`gameConfigs`; no route
      // of its own)
      if (resource === 'cabinets' && action === CONFIGURE_CABINETS_ACTION) {
        return {
          can: hasHostRole(
            await getMe(),
            getTenantId(),
            GAME_CONFIGURATION_ROLES,
          ),
        }
      }
      return { can: true }
    },
  }
}
